import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export type ProcedureTicketStatus = 'available' | 'reserved' | 'consumed' | 'expired' | 'cancelled';
export type ProcedureTicketSourceType = 'treatment_plan_item' | 'budget_item' | 'manual' | string;

export interface ProcedureTicketAllocation {
  id: string;
  ticket_id?: string;
  patient_id?: string;
  procedure_id: string;
  procedure_name?: string;
  unit_id?: string | null;
  unit_name?: string | null;
  source_type?: ProcedureTicketSourceType;
  source_id?: string | null;
  source_label?: string | null;
  stage?: string | null;
  stage_name?: string | null;
  total_sessions: number;
  available_sessions: number;
  reserved_sessions: number;
  consumed_sessions: number;
  expires_at?: string | null;
  status?: ProcedureTicketStatus;
  ticket_status?: string;
}

export interface ProcedureTicketBalance {
  available: number;
  reserved: number;
  consumed: number;
  total: number;
  allocations: ProcedureTicketAllocation[];
}

export interface ProcedureTicketHistoryEntry {
  id: string;
  ticket_id?: string;
  allocation_id?: string | null;
  appointment_id?: string | null;
  procedure_id?: string;
  procedure_name?: string;
  unit_name?: string | null;
  movement_type: 'credit' | 'reservation' | 'release' | 'consumption' | 'reversal' | string;
  quantity: number;
  stage?: string | null;
  source_label?: string | null;
  occurred_at?: string;
  created_at?: string;
  notes?: string | null;
}

export interface ProcedureTicketReservation {
  id: string;
  ticket_id?: string;
  allocation_id: string;
  appointment_id?: string | null;
  quantity: number;
  status: 'active' | 'consumed' | 'released' | 'cancelled' | string;
}

export interface TicketBalanceFilters {
  procedure_id?: string;
  unit_id?: string;
  appointment_id?: string;
  compatible_only?: boolean;
}

type ApiEnvelope<T> = { success?: boolean; data?: T; total?: number } | T;

const unwrap = <T,>(body: ApiEnvelope<T>): T => {
  if (body && typeof body === 'object' && 'data' in body && (body as any).data !== undefined) {
    return (body as any).data as T;
  }
  return body as T;
};

const asNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAllocation = (raw: any): ProcedureTicketAllocation => ({
  ...raw,
  id: String(raw?.id || raw?.allocation_id || raw?.ticket_id || ''),
  ticket_id: raw?.ticket_id || raw?.patient_procedure_ticket_id || raw?.id,
  procedure_id: String(raw?.procedure_id || raw?.clinic_procedure_id || ''),
  procedure_name: raw?.procedure_name || raw?.clinic_procedures?.name,
  unit_name: raw?.unit_name || raw?.clinic_units?.name,
  source_label: raw?.source_label
    || raw?.treatment_plan_title
    || raw?.budget_number
    || raw?.clinic_budgets?.budget_number,
  stage: raw?.stage || raw?.stage_name || raw?.default_stage || raw?.metadata?.stage,
  total_sessions: asNumber(raw?.total_sessions ?? raw?.quantity_granted ?? raw?.granted_quantity ?? raw?.quantity ?? raw?.total),
  available_sessions: asNumber(raw?.available_sessions ?? raw?.quantity_available ?? raw?.available_quantity ?? raw?.available ?? raw?.balance)
    || Math.max(
      0,
      asNumber(raw?.quantity_granted ?? raw?.granted_quantity)
        - asNumber(raw?.quantity_reserved ?? raw?.reserved_quantity)
        - asNumber(raw?.quantity_consumed ?? raw?.consumed_quantity),
    ),
  reserved_sessions: asNumber(raw?.reserved_sessions ?? raw?.quantity_reserved ?? raw?.reserved_quantity ?? raw?.reserved),
  consumed_sessions: asNumber(raw?.consumed_sessions ?? raw?.quantity_consumed ?? raw?.consumed_quantity ?? raw?.consumed ?? raw?.used),
  status: (raw?.status as ProcedureTicketStatus) || undefined,
  ticket_status: raw?.status ? String(raw.status) : undefined,
});

const normalizeBalance = (payload: any): ProcedureTicketBalance => {
  const rawAllocations = Array.isArray(payload)
    ? payload
    : payload?.allocations || payload?.tickets || payload?.items || [];
  const allocations = rawAllocations
    .map(normalizeAllocation)
    .filter((item: ProcedureTicketAllocation) => item.id)
    .filter((item: ProcedureTicketAllocation) => {
      const status = String(item.status || (item as any).ticket_status || '').toLowerCase();
      if (status === 'cancelled' || status === 'expired') return false;
      return true;
    });
  return {
    available: asNumber(payload?.available ?? payload?.available_sessions)
      || allocations.reduce((sum: number, item: ProcedureTicketAllocation) => sum + item.available_sessions, 0),
    reserved: asNumber(payload?.reserved ?? payload?.reserved_sessions)
      || allocations.reduce((sum: number, item: ProcedureTicketAllocation) => sum + item.reserved_sessions, 0),
    consumed: asNumber(payload?.consumed ?? payload?.consumed_sessions)
      || allocations.reduce((sum: number, item: ProcedureTicketAllocation) => sum + item.consumed_sessions, 0),
    total: asNumber(payload?.total ?? payload?.total_sessions ?? payload?.granted)
      || allocations.reduce((sum: number, item: ProcedureTicketAllocation) => sum + item.total_sessions, 0),
    allocations,
  };
};

export const patientProcedureTicketService = {
  async getBalance(patientId: string, filters: TicketBalanceFilters = {}): Promise<ProcedureTicketBalance> {
    const { data } = await axios.get(
      getApiUrl(`/clinic/procedure-tickets/patients/${patientId}`),
      { params: filters },
    );
    return normalizeBalance(unwrap(data));
  },

  async getHistory(patientId: string, params: { limit?: number; unit_id?: string } = {}): Promise<ProcedureTicketHistoryEntry[]> {
    const { data } = await axios.get(
      getApiUrl(`/clinic/procedure-tickets/patients/${patientId}/history`),
      { params },
    );
    const payload: any = unwrap(data);
    return (Array.isArray(payload) ? payload : payload?.items || payload?.history || []).map((entry: any) => ({
      ...entry,
      id: String(entry?.id || entry?.movement_id || ''),
      movement_type: entry?.movement_type || entry?.event_type || entry?.type || 'credit',
      quantity: asNumber(entry?.quantity ?? entry?.sessions ?? 1),
      occurred_at: entry?.occurred_at || entry?.created_at,
    }));
  },

  async reserve(payload: {
    appointment_id: string;
    patient_id: string;
    procedure_id: string;
    ticket_id?: string;
    quantity?: number;
  }): Promise<ProcedureTicketReservation> {
    const { appointment_id, ...body } = payload;
    const { data } = await axios.post(
      getApiUrl(`/clinic/procedure-tickets/appointments/${appointment_id}/reserve`),
      body,
    );
    return unwrap(data);
  },

  async release(appointmentId: string, reason?: string): Promise<void> {
    await axios.post(
      getApiUrl(`/clinic/procedure-tickets/appointments/${appointmentId}/release`),
      { reason },
    );
  },

  async consumeManual(
    patientId: string,
    payload: {
      items: Array<{ ticket_id: string; quantity: number }>;
      notes?: string;
      professional_id?: string;
      idempotency_key?: string;
    },
  ): Promise<{ consumed: Array<{ ticket_id: string; quantity: number }> }> {
    const { data } = await axios.post(
      getApiUrl(`/clinic/procedure-tickets/patients/${patientId}/consume-manual`),
      payload,
    );
    return unwrap(data);
  },
};

export default patientProcedureTicketService;
